/*
Author: Jude Fernando
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import Accordion from '@/components/Accordian/Accordian'
import PoaSurveyPrintContent from './PoaSurveyPdf'
import Alerts from '@/components/Molecule/Alerts'
import PoaHeader from '@/components/PoaHeader'
import FailModal from '@/components/PoaHeader/Complete/FailModal'
import IncompleteActionsModal from '@/components/PoaHeader/Complete/IncompleteActionsModal'
import PassModal from '@/components/PoaHeader/Complete/PassModal'
import Survey from '@/components/survey'
import { Tabs, constants } from '@/constants'
import { QuestionnaireResponseStatus } from '@/constants/enums'
import { RootState } from '@/reducers/store'
import { useResetExcludeOptionsList } from '@/state/survey/formInputSlice'
import { useApplyFieldsForNewPoaNodes } from '@/state/survey/formSlice'
import {
    useGetPoaSurvey,
    useGetQUestionAnswers,
    useGetWaitingListRecord,
    useResetPoaSurveyState,
    useSetResponseId,
    useUpdatePoaType,
} from '@/state/survey/poaSurveySlice'
import { isEmpty } from 'lodash'
import { Fragment, memo, useEffect, useRef, useState } from 'react'
import { generatePdf } from '@/utils/pdfUtils'
import { useSelector } from 'react-redux'

import { useNavigate, useParams } from 'react-router-dom'
import { INode } from '@/components/survey/SurveyModel/types/nodeTypes'
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import Close from '@/assets/Close';

const PatientSurvey: React.FC = memo(() => {
    //useSetSurveyState, setSurveyState
    const { questionnaireDetails, answersObj, alerts, actions, sectionValidations, sectionAlerts, waitingListRecord, isLoadingWaitingListRecord, isUpdatingPoaStatus } = useSelector(
        (state: RootState) => state.poaSurvey
    )

    const { questionnaireResponseId, questionnaireId, waitingListRecordId } = useParams()
    const getQuestionnaire = useGetPoaSurvey()
    const getWaitingListRecord = useGetWaitingListRecord()
    const setAnswerResponseId = useSetResponseId()
    const applyNewFieldValues = useApplyFieldsForNewPoaNodes()
    const resetPoaSurveyState = useResetPoaSurveyState()
    const resetExcludeOptionsList = useResetExcludeOptionsList()
    const getQuestionnaireAnswers = useGetQUestionAnswers()
    const navigate = useNavigate()

    const isInitialRunRetQuestionnaire = useRef(true)
    const [ready, setReady] = useState(false)
    const [tab, setTab] = useState<Tabs>(Tabs.Questionnaire)
    const prevTabRef = useRef<Tabs>(tab)
    const [isIncompleteActionsModalOpen, setIsIncompleteActionsModalOpen] = useState(false);
    const [isFailModalOpen, setIsFailModalOpen] = useState(false);
    const [isPassModalOpen, setIsPassModalOpen] = useState(false);
    const [sortedNodes, setSortedNodes] = useState<INode | null>(null);
    const [isPdfGenerating, setIsPdfGenerating] = useState(false);
    const printRef = useRef<HTMLDivElement>(null);
    const questionnaireFormRef = useRef<HTMLDivElement>(null);

    const handlePdfGeneration = async () => {
        if (printRef.current) {
            setIsPdfGenerating(true);
            await generatePdf(printRef.current);
            setSortedNodes(null);
            setIsPdfGenerating(false);
        }
    };

    const updatePoaStatusDispatch = useUpdatePoaType()

    const completeButtonEnabled = Object.keys(sectionValidations).length > 0 && Object.values(sectionValidations).every(Boolean);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        setTab(Tabs.Questionnaire)
        return () => {
            resetPoaSurveyState()
            resetExcludeOptionsList()
        }
    }, [])

    useEffect(() => {
        return () => {
            resetPoaSurveyState()
        }
    }, [navigate])

    useEffect(() => {
        if (
            isInitialRunRetQuestionnaire.current &&
            questionnaireResponseId &&
            questionnaireId
        ) {
            prevTabRef.current = tab
            setAnswerResponseId(parseInt(questionnaireResponseId, 10))
            getQuestionnaireAnswers(
                questionnaireResponseId,
                tab === Tabs.Patient ? true : false
            )
            const questionnaireIdnumber = Number(questionnaireId)
            getQuestionnaire(questionnaireIdnumber)
        }
    }, [questionnaireResponseId, questionnaireId])

    useEffect(() => {
        if (waitingListRecordId) {
            getWaitingListRecord(parseInt(waitingListRecordId))
        }
    }, [waitingListRecordId]);

    useEffect(() => {
        if (answersObj && questionnaireDetails && !ready) {
            prevTabRef.current = tab
            applyNewFieldValues(questionnaireDetails, answersObj.answers) // update form object with actual values
            setReady(true)
        }
    }, [questionnaireDetails, answersObj])

    // DO NOT REMOVE. this is for handling the tab between
    // patient answer and poa answer. keeping if needed again in the future.
    // const handleTabClick = (tab: string) => {
    //     resetQuestionnaireAnswers()
    //     resetExcludeOptionsList()
    //     setReady(false)
    //     getQuestionnaireAnswers(
    //         questionnaireResponseId,
    //         tab === Tabs.Patient ? true : false
    //     )
    //     setTab(tab as Tabs)
    //     selectTab(tab)
    // }

    // here action.type is a boolean that indicates whether the action is completed or not
    // this needs to be refactored to isComplete.
    const hasOutstandingActions = actions?.some((action) => action.type);
    const outComeSetToDiffer = waitingListRecord?.teamReviewOutcome.replace(/"/g, '') === constants.TeamReviewOutcomeDefer

    const onClickComplete = () => {
        sortNodesWithAlertsFirst();
        if (hasOutstandingActions || outComeSetToDiffer) {
            setIsIncompleteActionsModalOpen(true)
            return;
        }

        if (waitingListRecord?.teamReviewOutcome.replace(/"/g, '') === constants.TeamReviewOutcomeFail) {
            setIsFailModalOpen(true)
            return;
        }

        if (waitingListRecord?.teamReviewOutcome.replace(/"/g, '') === constants.TeamReviewOutcomePass) {
            setIsPassModalOpen(true)
            return;
        }

        if (!hasOutstandingActions && isEmpty(waitingListRecord?.teamReviewOutcome.replace(/"/g, ''))) {
            setIsIncompleteActionsModalOpen(true)
            return;
        }
    }

    const completeAndDownload = async (selectedDate?: string) => {
        if (waitingListRecordId && questionnaireResponseId) {
            const valuesToUpdate = [
                { "op": "replace", "path": "/StatusId", "value": QuestionnaireResponseStatus.Complete },
                { "op": "replace", "path": "/ExpirationDate", "value": selectedDate },
            ];
            await updatePoaStatusDispatch(
                waitingListRecordId as unknown as number,
                Number(questionnaireResponseId),
                valuesToUpdate,
                undefined,
            );
            setIsFailModalOpen(false);
            setIsPassModalOpen(false);
            toast(<span>
                <span className='highlight'>{`${waitingListRecord?.surname || ""}, ${waitingListRecord?.forename || ""} ${waitingListRecord?.chiNumber}`}</span>
                <span>{" "}</span>
                <span>{`${waitingListRecord?.teamReviewOutcome.replace(/"/g, '')} (Complete)`}</span>
            </span>, {
                duration: 5000
            });
            await getWaitingListRecord(parseInt(waitingListRecordId));
            setIsPdfGenerating(true);
            setTimeout(() => handlePdfGeneration(), 0);
        }
    }

    const injectConcernNode = (nodes: INode[]): void => {
        const teamReviewIndex = nodes.findIndex(node => node.name === constants.TeamReviewSection);

        const concernNode: INode = {
            name: "Concerns",
            type: "Concern",
            title: "POA: Concerns",
            nodes: [],
            configurations: [{
                key: "hasConcern",
                value: "true"
            }]
        };

        if (teamReviewIndex !== -1) {
            nodes.splice(teamReviewIndex + 1, 0, concernNode);
        }
    };

    const injectNoConcernNode = (nodes: INode[]): void => {
        // Find the index where nodes with no alerts start
        const firstNoAlertIndex = nodes.findIndex((node) => {
            // Skip TeamReviewSection and first Concerns node
            if (node.name === constants.TeamReviewSection || node.name === "Concerns") return false;
            return !sectionAlerts[node.name];
        });

        if (firstNoAlertIndex !== -1) {
            const secondConcernNode: INode = {
                name: "Concerns",
                type: "Concern",
                title: "POA: No concerns",
                nodes: [],
                configurations: [{
                    key: "hasConcern",
                    value: "false"
                }]
            };
            nodes.splice(firstNoAlertIndex, 0, secondConcernNode);
        }
    };

    const sortNodesWithAlertsFirst = () => {
        if (!questionnaireDetails?.nodes) return questionnaireDetails;

        /*const updateNodeTypes = (nodes: INode[]): INode[] => {
            return nodes.map(node => {
                const isPdfSingleSelect = false
                const hasConfiguredWidth = !!node.configurations?.some(
                    (config) =>
                        config.key === 'FieldWidthPx' ||
                        config.key === 'FieldWidthPct'
                )

                return {
                    ...node,
                    type: isPdfSingleSelect ? constants.SingleLineText : node.type,
                    // Preserve explicit width from questionnaire JSON (e.g. FieldWidthPct 0.5).
                    // Keep 128px only as fallback when no width is configured.
                    configurations: isPdfSingleSelect
                        ? hasConfiguredWidth
                            ? node.configurations
                            : [{ key: 'FieldWidthPx', value: '128' }]
                        : node.configurations,
                    nodes: node.nodes ? updateNodeTypes(node.nodes) : [],
                    repeatedNodes: node.repeatedNodes ? updateNodeTypes(node.repeatedNodes) : [],
                }
            });
        };*/
        
        const sortedNodes = [...questionnaireDetails.nodes]
            .filter(node => node.name !== constants.InstructionsAndInformationSection)
            .sort((a, b) => {
                // TeamReviewSection always comes first
                if (a.name === constants.TeamReviewSection) return -1;
                if (b.name === constants.TeamReviewSection) return 1;
                
                const aHasAlert = sectionAlerts[a.name] ? 1 : 0;
                const bHasAlert = sectionAlerts[b.name] ? 1 : 0;
                return bHasAlert - aHasAlert;
            });
        
        injectConcernNode(sortedNodes);
        injectNoConcernNode(sortedNodes);
        
        const updatedNodes = sortedNodes;
        setSortedNodes({ ...questionnaireDetails, nodes: updatedNodes });
    }

    const patientName = `${waitingListRecord?.forename || ""} ${waitingListRecord?.surname || ""}`;
    return (
        <Fragment>
            <div>
                <PoaHeader completeButtonEnabled={completeButtonEnabled} onClickComplete={onClickComplete} isLoading={isLoadingWaitingListRecord} waitingListRecord={waitingListRecord} isPdfGenerating={isPdfGenerating} />
                {/* <div className="tab main-tabs flex">
                    <div
                        className={`tablinks patient flex-none ${tab === 'patient' ? 'selected' : ''}`}
                        onClick={() => handleTabClick('patient')}
                    >
                        Patient answers
                    </div>
                    <div
                        className={`tablinks poa flex-none ${tab === 'questionnaire' ? 'selected' : ''}`}
                        onClick={() => handleTabClick('questionnaire')}
                    >
                        POA
                    </div>
                    <div className="tablinks empty flex-grow"></div>
                </div> */}

                <div className="alerts-accordion-wrap items-start flex">
                    <Alerts alerts={alerts} actions={actions} />
                    <Accordion level="parent" section="parent" />
                </div>

                {tab === 'patient' ? (
                    <div id="patient" className="tabcontent patient-tab">
                        {questionnaireDetails && ready ? (
                            <Survey
                                disabled={true}
                                config={questionnaireDetails}
                            ></Survey>
                        ) : null}
                    </div>
                ) : null}

                {tab === 'questionnaire' ? (
                    <div id="questionnaire" className="tabcontent" ref={questionnaireFormRef}>
                        {questionnaireDetails && ready ? (
                            <Survey
                                disabled={false}
                                config={questionnaireDetails}
                            ></Survey>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <PassModal disabled={isUpdatingPoaStatus} isOpen={isPassModalOpen} onClose={() => setIsPassModalOpen(false)} name={patientName} handleSubmit={completeAndDownload} />
            <FailModal disabled={isUpdatingPoaStatus} isOpen={isFailModalOpen} onClose={() => setIsFailModalOpen(false)} name={patientName} handleSubmit={completeAndDownload} />
            <IncompleteActionsModal
                isOpen={isIncompleteActionsModalOpen}
                onClose={() => setIsIncompleteActionsModalOpen(false)}
                name={patientName}
            />
            <Toaster containerStyle={{
                top: "55px",
            }} toastOptions={{ position: 'top-center', style: { height: "36px", padding: 0, margin: 0 } }}>
                {(t) => (
                    <ToastBar toast={t} style={{...t.style, padding: 0, margin: 0, maxWidth: 'none'}}>
                        {({ icon, message }) => (
                            <div className='poa-survey-toast-success'>
                                {icon}
                                <span className='text'>{message}</span>
                                <button onClick={() => toast.dismiss(t.id)} style={{ marginLeft: "12px", background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Close /></button>
                            </div>
                        )}
                    </ToastBar>
                )}
            </Toaster>
            <div style={{ display: 'block', height: 0, overflow: 'scroll' }}>
                { sortedNodes && ready && waitingListRecord && <PoaSurveyPrintContent ref={printRef} waitingListRecord={waitingListRecord} questionnaireDetails={sortedNodes} /> }
            </div>
        </Fragment>
    )
})

export default PatientSurvey
